import { Search, Xmark } from 'iconoir-react'
import { AnimatePresence, m } from 'motion/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useFocusTrap } from '@/lib/use-focus-trap'

type Item = { label: string; hint: string; action: () => void }

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

// A lightweight quick-nav (Ctrl/Cmd+K), not a full-text search index -- this
// is a four-page brochure site, so it points at the sections and contact
// actions that actually exist rather than faking a search backend.
export function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const navigate = useNavigate()
  const panelRef = useRef<HTMLDivElement>(null)
  useFocusTrap(panelRef, open)

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((v) => !v)
      }
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    if (!open) return
    setQuery('')
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  function go(id: string) {
    if (window.location.pathname !== '/') {
      navigate('/')
      requestAnimationFrame(() => scrollToId(id))
    } else {
      scrollToId(id)
    }
    setOpen(false)
  }

  const items: Item[] = useMemo(
    () => [
      { label: 'Home', hint: 'Top', action: () => go('main-top') },
      { label: 'What We Do', hint: 'Page', action: () => { navigate('/what-we-do'); setOpen(false) } },
      { label: 'FAQ', hint: 'Page', action: () => { navigate('/faq'); setOpen(false) } },
      { label: "What's in the dashboard", hint: 'Section', action: () => go('dashboard') },
      { label: 'What happens next', hint: 'Section', action: () => go('next') },
      { label: 'Get started', hint: 'Page', action: () => { navigate('/contact'); setOpen(false) } },
      { label: 'Call (956) 887-1317', hint: 'Phone', action: () => (window.location.href = 'tel:+19568871317') },
      { label: 'Email FlowScale1@outlook.com', hint: 'Email', action: () => (window.location.href = 'mailto:FlowScale1@outlook.com') },
      { label: 'Privacy Policy', hint: 'Page', action: () => { navigate('/privacy-policy'); setOpen(false) } },
      { label: 'Terms of Service', hint: 'Page', action: () => { navigate('/terms-of-service'); setOpen(false) } },
    ],
    [navigate],
  )

  const filtered = items.filter((item) => item.label.toLowerCase().includes(query.toLowerCase()))

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-cursor="link"
        aria-label="Search"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-n-500 transition-colors hover:bg-n-400 hover:text-ink"
      >
        <Search width={17} height={17} />
      </button>
      {createPortal(
        <AnimatePresence>
          {open && (
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 flex items-start justify-center bg-n-100/70 px-4 pt-[14vh]"
            onClick={() => setOpen(false)}
          >
            <m.div
              ref={panelRef}
              role="dialog"
              aria-modal="true"
              aria-label="Quick navigation"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-[560px] rounded-lg border border-n-200 bg-n-300"
            >
              <div className="flex items-center gap-3 border-b border-n-200 px-5 py-4">
                <Search width={18} height={18} className="shrink-0 text-n-500" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Jump to a section, or call / email us"
                  className="w-full bg-transparent text-body text-ink placeholder:text-n-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close search"
                  className="shrink-0 text-n-500 hover:text-ink"
                >
                  <Xmark width={18} height={18} />
                </button>
              </div>
              <ul className="max-h-[50vh] overflow-y-auto p-2">
                {filtered.length === 0 && (
                  <li className="px-3 py-6 text-center text-body text-n-500">No matches.</li>
                )}
                {filtered.map((item) => (
                  <li key={item.label}>
                    <button
                      type="button"
                      onClick={item.action}
                      className="flex w-full items-center justify-between rounded-md px-3 py-3 text-left text-body text-ink transition-colors hover:bg-n-400"
                    >
                      {item.label}
                      <span className="text-meta text-n-500">{item.hint}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </m.div>
          </m.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  )
}
